import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CUSTOM_PLAN, getPlan } from "@/lib/plans";
import { goToPayPage } from "@/lib/payHost";
import { loadCheckoutDetails } from "../checkoutDetails";
import { currentLanguage } from "@/i18n";

/**
 * Legacy route. New checkouts go straight to `{PUBLIC_PAY_URL}/pay/{id}`.
 * Bookmarks still work: we create the pay session from stored details.
 */
export default function PaymentMethodPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const started = useRef(false);

  const plan = useMemo(() => getPlan(searchParams.get("plan")), [searchParams]);
  const amount = searchParams.get("amount");
  const isCustom = searchParams.get("plan")?.toLowerCase() === CUSTOM_PLAN.toLowerCase();
  const tier = isCustom ? CUSTOM_PLAN : plan.name;
  const checkoutUrl = isCustom
    ? `/checkout?plan=${CUSTOM_PLAN}&amount=${encodeURIComponent(amount ?? "")}`
    : `/checkout?plan=${encodeURIComponent(plan.name)}`;

  useEffect(() => {
    if (started.current) return;

    const details = loadCheckoutDetails();
    if (!details) {
      navigate(checkoutUrl, { replace: true });
      return;
    }

    started.current = true;
    api
      .post<{ payId: string; payUrl: string }>("/payments/session", {
        tier,
        amount: isCustom ? (amount ?? details.amount) : undefined,
        customer: details.customer,
        // Only true if the box was ticked on /checkout; otherwise the server
        // refuses and the customer is sent back to the form to tick it.
        acceptTerms: details.acceptTerms,
        language: currentLanguage(),
      })
      .then((session) => goToPayPage(session.payUrl, navigate))
      .catch(() => navigate(checkoutUrl, { replace: true }));
  }, [navigate, checkoutUrl, tier, isCustom, amount]);

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center">
      <i className="ri-loader-4-line text-3xl text-primary-600 animate-spin"></i>
    </div>
  );
}
